/**
 * Utility function to check if a freelancer's KYC is complete
 *
 * For French freelancers:
 * - KYC is green if ID, Kbis/RNE and Attestation de Vigilance have been provided
 * - Attestation de Vigilance: valid_date or created_at + 6 months
 * - RC Pro (when required by team): document present and not rejected; if valid_date is set, end of coverage must be >= today (no +6 month fallback). Absence of valid_date counts as valid (e.g. extraction failed but doc approved).
 * - ID/passport or driver-license-front for identity (when team allows driver as identity)
 * - Driver license front + back (category B on back) required when allow_driver_license is on
 *
 * For non-French freelancers:
 * - KYC is green if ID and Registration Document have been provided
 * - RC Pro (when required by team): same rules as French freelancers
 * - No date validation for ID / registration documents
 *
 * For portage salarial (umbrella):
 * - KYC is based on portage company documents (registre-entreprises, attestation-vigilance, RC Pro if required)
 * - CV, portfolio, diplomas, criminal record, driver license and personal ID do not apply
 *
 * @param {Object} freelancer - The freelancer object
 * @param {string} freelancer.country - The freelancer's country
 * @param {string} freelancer.legal_entity_type - The legal entity type ('company' or 'auto-entrepreneur')
 * @param {Array} freelancer.legal_documents - Array of legal documents
 * @param {boolean} [freelancer.require_rc_insurance] - Team requires RC Pro (detail / list API)
 * @param {{ require_rc_insurance?: boolean }} [freelancer.team_settings] - From /freelancers/me
 * @returns {boolean} - true if KYC is complete, false otherwise
 */

import { isPortageCompanyKycComplete, normalizePortageKycCategory } from '@portage-kyc'
import { isPortageAffiliationPending } from './portageAffiliationPending'

/** @param {Array<{ category?: string, file_name?: string, file_key?: string, valid_until?: string | null, valid_date?: string | null }>} documents */
function mapPortageCompanyDocumentsForKyc(documents) {
  return (Array.isArray(documents) ? documents : []).map((doc) => ({
    category: normalizePortageKycCategory(String(doc.category || '')),
    file_name: doc.file_name,
    file_key: doc.file_key,
    valid_until: doc.valid_until ?? doc.valid_date ?? null,
    validation_status: doc.validation_status ?? 'pending_review',
  }))
}

/** @param {Record<string, unknown> | null | undefined} freelancer */
export function requiresRcInsurance(freelancer) {
  if (!freelancer) return false
  if (freelancer.require_rc_insurance === true) return true
  const ts = freelancer.team_settings
  if (ts && typeof ts === 'object' && ts.require_rc_insurance === true) return true
  return false
}

/**
 * @param {Record<string, unknown> | null | undefined} freelancer
 */
export function requiresNdaActivityReceipt(freelancer) {
  if (!freelancer) return false
  if (freelancer.require_nda_activity_receipt === true) return true
  const ts = freelancer.team_settings
  if (ts && typeof ts === 'object' && ts.require_nda_activity_receipt === true) return true
  return false
}

/**
 * Attestation de régularité fiscale (France, opt-in équipe).
 * @param {Record<string, unknown> | null | undefined} freelancer
 */
export function requiresTaxRegularityAttestation(freelancer) {
  if (!freelancer) return false
  if (freelancer.require_tax_regularity_attestation === true) return true
  const ts = freelancer.team_settings
  if (ts && typeof ts === 'object' && ts.require_tax_regularity_attestation === true) return true
  return false
}

/**
 * @param {Record<string, unknown> | null | undefined} freelancer
 */
export function allowsDriverLicense(freelancer) {
  if (!freelancer) return false
  if (freelancer.allow_driver_license === true) return true
  const ts = freelancer.team_settings
  if (ts && typeof ts === 'object' && ts.allow_driver_license === true) return true
  return false
}

/**
 * @param {Record<string, unknown> | null | undefined} freelancer
 */
export function requiresCriminalRecord(freelancer) {
  if (!freelancer) return false
  if (freelancer.require_criminal_record === true) return true
  const ts = freelancer.team_settings
  if (ts && typeof ts === 'object' && ts.require_criminal_record === true) return true
  return false
}

/**
 * CV demandé par l'équipe (un seul document, aucune vérification).
 * @param {Record<string, unknown> | null | undefined} freelancer
 */
export function requiresCv(freelancer) {
  if (!freelancer) return false
  if (freelancer.require_cv === true) return true
  const ts = freelancer.team_settings
  if (ts && typeof ts === 'object' && ts.require_cv === true) return true
  return false
}

/**
 * Portfolio demandé par l'équipe (un seul document, aucune vérification).
 * @param {Record<string, unknown> | null | undefined} freelancer
 */
export function requiresPortfolio(freelancer) {
  if (!freelancer) return false
  if (freelancer.require_portfolio === true) return true
  const ts = freelancer.team_settings
  if (ts && typeof ts === 'object' && ts.require_portfolio === true) return true
  return false
}

/**
 * Diplômes demandés par l'équipe (au moins un document, aucune vérification).
 * @param {Record<string, unknown> | null | undefined} freelancer
 */
export function requiresDiplomas(freelancer) {
  if (!freelancer) return false
  if (freelancer.require_diplomas === true) return true
  const ts = freelancer.team_settings
  if (ts && typeof ts === 'object' && ts.require_diplomas === true) return true
  return false
}

/**
 * CV : présent et non rejeté suffit (auto-approuvé à l'upload, pas de vérification).
 * @param {Array<{ category?: string, validation_status?: string }>} documents
 */
export function hasValidCv(documents) {
  if (!Array.isArray(documents)) return false
  return documents.some((d) => d && d.category === 'cv' && d.validation_status !== 'rejected')
}

/**
 * Portfolio : présent et non rejeté suffit (auto-approuvé à l'upload, pas de vérification).
 * @param {Array<{ category?: string, validation_status?: string }>} documents
 */
export function hasValidPortfolio(documents) {
  if (!Array.isArray(documents)) return false
  return documents.some((d) => d && d.category === 'portfolio' && d.validation_status !== 'rejected')
}

/**
 * Diplômes : au moins un document présent et non rejeté.
 * @param {Array<{ category?: string, validation_status?: string }>} documents
 */
export function hasValidDiplomas(documents) {
  if (!Array.isArray(documents)) return false
  return documents.some((d) => d && d.category === 'diploma' && d.validation_status !== 'rejected')
}

export const DRIVER_LICENSE_FRONT_CATEGORY = 'driver-license-front'
export const DRIVER_LICENSE_BACK_CATEGORY = 'driver-license-back'
/** @deprecated Migré vers driver-license-front */
export const DRIVER_LICENSE_LEGACY_CATEGORY = 'driver-license'

/** Permis de conduire requis quand l'équipe active allow_driver_license (comme RC Pro). */
export function requiresDriverLicense(freelancer) {
  return allowsDriverLicense(freelancer)
}

/** @param {Array<{ category?: string }>} documents */
export function findDriverLicenseFrontDocument(documents) {
  if (!Array.isArray(documents)) return undefined
  return (
    documents.find((d) => d && d.category === DRIVER_LICENSE_FRONT_CATEGORY) ||
    documents.find((d) => d && d.category === DRIVER_LICENSE_LEGACY_CATEGORY)
  )
}

/** @param {Array<{ category?: string }>} documents */
export function findDriverLicenseBackDocument(documents) {
  if (!Array.isArray(documents)) return undefined
  return documents.find((d) => d && d.category === DRIVER_LICENSE_BACK_CATEGORY)
}

export function isDriverLicenseRequirementMet(freelancer, todayRef) {
  if (!requiresDriverLicense(freelancer)) return true
  const documents = Array.isArray(freelancer.legal_documents) ? freelancer.legal_documents : []
  const front = findDriverLicenseFrontDocument(documents)
  const back = findDriverLicenseBackDocument(documents)
  return (
    isDriverLicenseDocumentValid(front, todayRef) && isDriverLicenseBackDocumentValid(back)
  )
}

/**
 * Pièce d'identité (scanned-id-card) : passeport / CNI.
 * @param {Record<string, unknown> | null | undefined} document
 */
export function isIdentityDocumentValid(document, _freelancer, todayRef) {
  if (!document) return false
  if (document.validation_status === 'rejected') return false
  if (document.valid_date) {
    const today = todayRef ? new Date(todayRef) : new Date()
    today.setHours(0, 0, 0, 0)
    const expirationDate = new Date(String(document.valid_date))
    expirationDate.setHours(0, 0, 0, 0)
    if (expirationDate < today) return false
  }
  return true
}

/**
 * Permis recto (driver-license-front).
 * @param {Record<string, unknown> | null | undefined} document
 */
export function isDriverLicenseDocumentValid(document, todayRef) {
  if (!document) return false
  if (document.validation_status === 'rejected') return false
  if (document.valid_date) {
    const today = todayRef ? new Date(todayRef) : new Date()
    today.setHours(0, 0, 0, 0)
    const expirationDate = new Date(String(document.valid_date))
    expirationDate.setHours(0, 0, 0, 0)
    if (expirationDate < today) return false
  }
  return true
}

/**
 * Permis verso : approuvé après vérif Mindee (catégorie B).
 * @param {Record<string, unknown> | null | undefined} document
 */
export function isDriverLicenseBackDocumentValid(document) {
  if (!document) return false
  if (document.validation_status !== 'approved') return false
  const verificationData = document.verification_data
  if (verificationData && typeof verificationData === 'object' && Array.isArray(verificationData.checks)) {
    const categoryCheck = verificationData.checks.find((c) => c && c.rule === 'category_b')
    if (categoryCheck && categoryCheck.passed === false) return false
  }
  return true
}

/**
 * Preuve d'identité : scanned-id-card OU permis recto si l'équipe autorise.
 * @param {Record<string, unknown> | null | undefined} freelancer
 */
export function hasValidIdentityProof(freelancer, todayRef) {
  if (!freelancer) return false
  const documents = Array.isArray(freelancer.legal_documents) ? freelancer.legal_documents : []
  const today = todayRef || new Date()

  if (
    isIdentityDocumentValid(
      documents.find((doc) => doc && doc.category === 'scanned-id-card'),
      freelancer,
      today,
    )
  ) {
    return true
  }
  if (allowsDriverLicense(freelancer)) {
    return isDriverLicenseDocumentValid(findDriverLicenseFrontDocument(documents), today)
  }
  return false
}

/**
 * RC Pro: rejected documents are invalid. If valid_date is missing, treat as valid (file present).
 * If valid_date is set (extraction), end of coverage must be >= today.
 * @param {Record<string, unknown> | null | undefined} document
 * @param {Date} [todayRef] - start of today for tests
 */
export function isRcInsuranceValid(document, todayRef) {
  if (!document) return false
  if (document.validation_status === 'rejected') return false
  if (!document.valid_date) return true
  const today = todayRef ? new Date(todayRef) : new Date()
  today.setHours(0, 0, 0, 0)
  const expirationDate = new Date(String(document.valid_date))
  expirationDate.setHours(0, 0, 0, 0)
  return expirationDate >= today
}

/**
 * NDA récépissé : même règle que RC Pro (pas de valid_date obligatoire en v1).
 * @param {Record<string, unknown> | null | undefined} document
 * @param {Date} [todayRef]
 */
export function isNdaActivityReceiptValid(document, todayRef) {
  return isRcInsuranceValid(document, todayRef)
}

/** Après acceptation : valable 11 mois à compter de la date de délivrance (aligné backend). */
export const TAX_REGULARITY_VALIDITY_MONTHS = 11

/**
 * Attestation de régularité fiscale : approuvée et encore dans sa période de validité.
 * valid_date = date de délivrance ; fin de validité = délivrance + 11 mois.
 * @param {Record<string, unknown> | null | undefined} document
 * @param {Date} [todayRef]
 */
export function isTaxRegularityAttestationValid(document, todayRef) {
  if (!document) return false
  if (document.validation_status === 'rejected') return false
  if (document.validation_status !== 'approved') return false

  const today = todayRef ? new Date(todayRef) : new Date()
  today.setHours(0, 0, 0, 0)

  const deliveryDate = document.valid_date
    ? String(document.valid_date)
    : (() => {
        const extraction = document?.verification_data?.extraction
        if (!extraction || typeof extraction !== 'object') return null
        const issueDate = extraction.issueDate ?? extraction.issue_date
        return typeof issueDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(issueDate)
          ? issueDate
          : null
      })()

  if (!deliveryDate) return true

  const [y, m, d] = deliveryDate.split('-').map(Number)
  const baseDate = new Date(y, m - 1, d)
  if (Number.isNaN(baseDate.getTime())) return true

  baseDate.setHours(0, 0, 0, 0)
  const validityLimit = new Date(baseDate)
  validityLimit.setMonth(validityLimit.getMonth() + TAX_REGULARITY_VALIDITY_MONTHS)
  validityLimit.setHours(0, 0, 0, 0)
  return today <= validityLimit
}

/** @param {Record<string, unknown> | null | undefined} document */
function getTaxRegularityDeliveryDateFromVerification(document) {
  const extraction = document?.verification_data?.extraction
  if (!extraction || typeof extraction !== 'object') return null
  const issueDate = extraction.issueDate ?? extraction.issue_date
  if (!issueDate || typeof issueDate !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(issueDate)) {
    return null
  }
  return issueDate
}

/** Fin de validité affichable : date de délivrance + 11 mois. */
export function getTaxRegularityAttestationExpirationDate(document) {
  if (!document) return null

  const deliveryYmd = document.valid_date
    ? String(document.valid_date)
    : getTaxRegularityDeliveryDateFromVerification(document)
  if (!deliveryYmd || !/^\d{4}-\d{2}-\d{2}$/.test(deliveryYmd)) return null

  const [y, m, d] = deliveryYmd.split('-').map(Number)
  const expiry = new Date(y, m - 1 + TAX_REGULARITY_VALIDITY_MONTHS, d)
  const yy = expiry.getFullYear()
  const mm = String(expiry.getMonth() + 1).padStart(2, '0')
  const dd = String(expiry.getDate()).padStart(2, '0')
  return `${yy}-${mm}-${dd}`
}

/**
 * Bulletin n°3 : approuvé + délivré depuis moins de 3 mois (valid_date = date de délivrance).
 * @param {Record<string, unknown> | null | undefined} document
 * @param {Date} [todayRef]
 */
export function isCriminalRecordValid(document, todayRef) {
  if (!document) return false
  if (document.validation_status === 'rejected') return false
  if (document.validation_status !== 'approved') return false

  const today = todayRef ? new Date(todayRef) : new Date()
  today.setHours(0, 0, 0, 0)

  const baseDate = document.valid_date
    ? new Date(String(document.valid_date))
    : document.created_at
      ? new Date(String(document.created_at))
      : null

  if (!baseDate) return true

  const freshnessLimit = new Date(baseDate)
  freshnessLimit.setMonth(freshnessLimit.getMonth() + 3)
  freshnessLimit.setHours(0, 0, 0, 0)
  return today <= freshnessLimit
}

/**
 * Le freelance hors-UE doit avoir répondu à la question "exercez-vous depuis la France ?".
 * @param {Record<string, unknown> | null | undefined} freelancer
 */
export function requiresWorkLocationAnswer(freelancer) {
  return !!freelancer && freelancer.requires_work_location_question === true
}

/** @param {Record<string, unknown> | null | undefined} freelancer */
export function hasAnsweredWorkLocation(freelancer) {
  if (!freelancer) return false
  return freelancer.works_from_france === true || freelancer.works_from_france === false
}

/** @param {Record<string, unknown> | null | undefined} freelancer */
export function requiresResidencePermit(freelancer) {
  return !!freelancer && freelancer.requires_residence_permit === true
}

/**
 * Titre de séjour : présent, non rejeté, non expiré (valid_date >= aujourd'hui).
 * Le statut reste pending_review (revue super-admin), donc on n'exige pas "approved".
 * @param {Record<string, unknown> | null | undefined} document
 * @param {Date} [todayRef]
 */
export function isResidencePermitValid(document, todayRef) {
  if (!document) return false
  if (document.validation_status === 'rejected') return false
  if (document.valid_date) {
    const today = todayRef ? new Date(todayRef) : new Date()
    today.setHours(0, 0, 0, 0)
    const expirationDate = new Date(String(document.valid_date))
    expirationDate.setHours(0, 0, 0, 0)
    if (expirationDate < today) return false
  }
  return true
}

export const isKYCComplete = (freelancer) => {
  if (!freelancer) {
    return false
  }

  const { country, legal_entity_type, legal_documents = [] } = freelancer

  if (legal_entity_type === 'portage-salarial') {
    if (isPortageAffiliationPending(freelancer)) return false
    const portageDocs = freelancer.portage_company_legal_documents
    if (Array.isArray(portageDocs) && portageDocs.length > 0) {
      const portageCountry = freelancer.portage_company?.country ?? freelancer.portage_company_country ?? country
      return isPortageCompanyKycComplete(
        portageCountry,
        mapPortageCompanyDocumentsForKyc(portageDocs),
        { requireRcInsurance: requiresRcInsurance(freelancer) }
      )
    }
    if (typeof freelancer.portage_kyc_complete === 'boolean') {
      return freelancer.portage_kyc_complete
    }
    return false
  }

  const isFrance = country === 'France'
  const isCompany = legal_entity_type === 'company'
  const isAutoEntrepreneur = legal_entity_type === 'auto-entrepreneur'

  const documents = Array.isArray(legal_documents) ? legal_documents : []

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const documentExists = (document) => {
    if (!document) return false
    if (document.validation_status === 'rejected') return false
    return true
  }

  const isAttestationValid = (document) => {
    if (!document) return false
    if (document.validation_status === 'rejected') return false

    // Self-attestation on honor (signed via Yousign) only counts as valid once
    // the signature webhook has marked the doc 'approved'. Pre-signature, the
    // row exists but the user hasn't signed yet — treat as absent so KYC stays red
    // and we don't show a misleading "pending review".
    const isSelfAttestation =
      document.verification_data &&
      document.verification_data.kind === 'self_attestation_non_remuneration'
    if (isSelfAttestation && document.validation_status !== 'approved') {
      return false
    }

    if (document.valid_date) {
      const expirationDate = new Date(document.valid_date)
      expirationDate.setHours(0, 0, 0, 0)
      return expirationDate >= today
    }

    if (document.created_at) {
      const uploadDate = new Date(document.created_at)
      const expirationDate = new Date(uploadDate)
      expirationDate.setMonth(expirationDate.getMonth() + 6)
      expirationDate.setHours(0, 0, 0, 0)
      return expirationDate >= today
    }

    return false
  }

  const findDocument = (category) => {
    return documents.find((doc) => doc && doc.category === category)
  }

  const rcRequired = requiresRcInsurance(freelancer)
  const ndaRequired = requiresNdaActivityReceipt(freelancer)
  const taxRegularityRequired = requiresTaxRegularityAttestation(freelancer)
  const casierRequired = requiresCriminalRecord(freelancer)
  const driverOk = isDriverLicenseRequirementMet(freelancer, today)
  const cvOk = !requiresCv(freelancer) || hasValidCv(documents)
  const portfolioOk = !requiresPortfolio(freelancer) || hasValidPortfolio(documents)
  const diplomasOk = !requiresDiplomas(freelancer) || hasValidDiplomas(documents)

  // Titre de séjour (hors-UE travaillant depuis la France) :
  // - la question doit avoir été répondue si elle est requise ;
  // - si works_from_france === true, un titre de séjour valide est exigé.
  const workLocationOk = !requiresWorkLocationAnswer(freelancer) || hasAnsweredWorkLocation(freelancer)
  const residencePermitOk =
    !requiresResidencePermit(freelancer) || isResidencePermitValid(findDocument('residence-permit'), today)
  const residenceOk = workLocationOk && residencePermitOk

  let result = false

  if (isFrance && isAutoEntrepreneur) {
    const rneExists = documentExists(findDocument('registre-entreprises'))
    const attestationValid = isAttestationValid(findDocument('attestation-vigilance'))
    const idExists = hasValidIdentityProof(freelancer, today)
    const rcOk = !rcRequired || isRcInsuranceValid(findDocument('rc-insurance'), today)
    const ndaOk = !ndaRequired || isNdaActivityReceiptValid(findDocument('nda-activity-receipt'), today)
    const taxRegularityOk =
      !taxRegularityRequired ||
      isTaxRegularityAttestationValid(findDocument('tax-regularity-attestation'), today)
    const casierOk = !casierRequired || isCriminalRecordValid(findDocument('casier-judiciaire'), today)

    result = rneExists && attestationValid && idExists && driverOk && rcOk && ndaOk && taxRegularityOk && casierOk && cvOk && portfolioOk && diplomasOk && residenceOk
  } else if (isFrance && isCompany) {
    const kbisExists = documentExists(findDocument('registre-entreprises'))
    const attestationValid = isAttestationValid(findDocument('attestation-vigilance'))
    const idExists = hasValidIdentityProof(freelancer, today)
    const rcOk = !rcRequired || isRcInsuranceValid(findDocument('rc-insurance'), today)
    const ndaOk = !ndaRequired || isNdaActivityReceiptValid(findDocument('nda-activity-receipt'), today)
    const taxRegularityOk =
      !taxRegularityRequired ||
      isTaxRegularityAttestationValid(findDocument('tax-regularity-attestation'), today)
    const casierOk = !casierRequired || isCriminalRecordValid(findDocument('casier-judiciaire'), today)

    result = kbisExists && attestationValid && idExists && driverOk && rcOk && ndaOk && taxRegularityOk && casierOk && cvOk && portfolioOk && diplomasOk && residenceOk
  } else {
    const idExists = hasValidIdentityProof(freelancer, today)
    const registrationExists = documentExists(findDocument('registre-entreprises'))
    const rcOk = !rcRequired || isRcInsuranceValid(findDocument('rc-insurance'), today)

    result = idExists && registrationExists && driverOk && rcOk && cvOk && portfolioOk && diplomasOk && residenceOk
  }

  return result
}

/**
 * Date d'expiration affichable pour les documents avec validité (YYYY-MM-DD ou null).
 * @param {Record<string, unknown> | null | undefined} document
 */
export function getLegalDocumentExpirationDate(document) {
  if (!document) return null
  if (
    document.category === 'attestation-vigilance' &&
    document.validation_status === 'rejected'
  ) {
    return null
  }
  if (
    document.category === 'rc-insurance' ||
    document.category === 'scanned-id-card' ||
    document.category === 'residence-permit' ||
    document.category === 'tax-regularity-attestation' ||
    document.category === DRIVER_LICENSE_LEGACY_CATEGORY ||
    document.category === DRIVER_LICENSE_FRONT_CATEGORY
  ) {
    if (document.category === 'tax-regularity-attestation') {
      return getTaxRegularityAttestationExpirationDate(document)
    }
    return document.valid_date ? String(document.valid_date) : null
  }
  if (document.category !== 'attestation-vigilance') return null
  if (document.valid_date) return String(document.valid_date)
  if (document.created_at) {
    const uploadDate = new Date(String(document.created_at))
    const expirationDate = new Date(uploadDate)
    expirationDate.setMonth(expirationDate.getMonth() + 6)
    return expirationDate.toISOString().split('T')[0]
  }
  return null
}

/**
 * True when the document was approved but its validity period has ended.
 * @param {Record<string, unknown> | null | undefined} document
 * @param {Date} [todayRef]
 */
export function isLegalDocumentExpired(document, todayRef) {
  if (!document || document.validation_status !== 'approved') return false
  const expCategories = new Set([
    'attestation-vigilance',
    'rc-insurance',
    'scanned-id-card',
    'residence-permit',
    'tax-regularity-attestation',
    DRIVER_LICENSE_LEGACY_CATEGORY,
    DRIVER_LICENSE_FRONT_CATEGORY,
  ])
  if (!expCategories.has(String(document.category))) return false
  const expDate = getLegalDocumentExpirationDate(document)
  if (!expDate) return false
  const today = todayRef ? new Date(todayRef) : new Date()
  today.setHours(0, 0, 0, 0)
  const dayOnly = expDate.includes('T') ? expDate.split('T')[0] : expDate
  const expirationDate = new Date(dayOnly)
  expirationDate.setHours(0, 0, 0, 0)
  return expirationDate < today
}
